import { useRef } from 'react';
import { io, type Socket } from 'socket.io-client';

import type { Notification } from '../types/notification.types';

export interface SocketEventHandlers {
  onNotification: (notification: Notification) => void;
  onConnect: () => void;
  onDisconnect: () => void;
  onError: (error: Error) => void;
}

export class SocketService {
  private socket: Socket | null = null;
  private connectionAttempts = 0;

  connect(
    userId: string,
    baseUrl: string,
    handlers: SocketEventHandlers,
  ): Socket {
    if (this.socket) {
      return this.socket;
    }

    const socket = io(`${baseUrl}/notifications`, {
      path: '/socket.io',
      query: { userId },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      extraHeaders: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Credentials': 'true',
      },
    });

    this.socket = socket;

    socket.on('connect', () => {
      socket.emit('subscribe', { userId });
      this.connectionAttempts = 0;
      handlers.onConnect();
    });

    socket.on('notification', handlers.onNotification);

    socket.on('disconnect', () => {
      console.warn('❌ Disconnected from notification server');
      handlers.onDisconnect();
    });

    socket.on('connecterror', (error: Error) => {
      console.error('Socket error:', error.message);
      this.connectionAttempts++;
      handlers.onError(error);
    });

    return socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  getConnectionAttempts(): number {
    return this.connectionAttempts;
  }

  resetConnectionAttempts(): void {
    this.connectionAttempts = 0;
  }
}

export const useSocketService = () => {
  const socketServiceRef = useRef<SocketService | null>(null);

  if (!socketServiceRef.current) {
    socketServiceRef.current = new SocketService();
  }

  return socketServiceRef.current;
};
